import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { toLegDetail, type LegDetailWire } from '@/features/cases/api/caseWire'
import type { CaseLeg, LegPointsPayload } from '@/types/domain'

/* One leg as its own page reads and writes it (issue #35, story 3).
 *
 * Separate from `useCase.ts`, which owns the leg writes the CASE screen makes
 * (add, amend, remove), because these three are the page's own surface: a
 * composed single-leg read, the chain replacement behind «Груз в …», and the
 * template apply. Keeping them here is not tidiness — `useCase.ts` is the case
 * record's file, and a reader looking for what the leg page can do should not
 * have to know that half of it lives under the case.
 *
 * ── Nothing here decides where the cargo is ────────────────────────────────
 *
 * The chain's current point is `currentPointId`, served. This file never
 * computes it, never patches it into the cache, and never marks a point reached
 * ahead of the server's answer. That is `legnow`'s rule about the current LEG
 * restated one level down, and the failure it prevents is the expensive one in
 * this product: a screen that says the client's cargo is somewhere the database
 * does not.
 *
 * ── Why every write here just invalidates ──────────────────────────────────
 *
 * Each of the two writes answers with the whole leg, and it is tempting to seed
 * it into the page's key. It must not be: the page reads the COMPOSED leg
 * (`readmodel_leg.go`), and story 6 HAS joined the leg's money onto that
 * composition. A write handler answers the plain leg DTO, so seeding it would
 * silently replace a composed answer with a narrower one — the money would
 * vanish from the page until something unrelated refetched it. Invalidation
 * costs one round trip and cannot go stale in a way nobody notices.
 *
 * `toLegDetail` is the mapper for both, and it is written so that the narrower
 * answer cannot be mistaken for a failure either: a body carrying neither
 * `money` nor `moneyUnavailable` maps to a leg with no money block rather than
 * to one whose money "could not be read". See its own note.
 */

/** The composed leg — the page's read.
 *
 *  `enabled` on BOTH ids for the reason `useInvoice` gives: no leg chosen yet
 *  is not an empty leg, and firing `/cases//legs/` would ask the server a
 *  question about nothing and render its 404 as a failure. */
export function useCaseLeg(caseId: string, legId: string) {
  return useQuery<CaseLeg>({
    queryKey: queryKeys.cases.leg(caseId, legId),
    enabled: caseId !== '' && legId !== '',
    queryFn: async () =>
      toLegDetail(await api.get<LegDetailWire>(`/cases/${caseId}/legs/${legId}`)),
  })
}

/** Everything a leg write moves, refreshed.
 *
 *  The page's own key is NOT named here, and that is the point of hanging it
 *  under `cases.detail` (see `lib/query.ts`): invalidation matches by prefix, so
 *  the case detail carries the open leg page with it. The board's row goes too
 *  — it reads the case's position off the composed list row, and a chain that
 *  moved is a position that moved. */
function useInvalidateLeg(caseId: string) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
  }
}

/** Replaces the leg's route chain whole (PUT .../points).
 *
 *  Whole-list, like every other list on a leg: the caller submits the chain it
 *  wants, and marking the next point reached is that same submission with one
 *  more `reached: true` on it. There is deliberately no "advance by one"
 *  endpoint — one write shape means one place the prefix rule is enforced, on
 *  the server, rather than a second one here that could disagree. */
export function useReplaceLegPoints(caseId: string, legId: string) {
  const invalidate = useInvalidateLeg(caseId)

  return useMutation<CaseLeg, Error, LegPointsPayload>({
    mutationFn: async (payload) =>
      toLegDetail(
        await api.put<LegDetailWire>(`/cases/${caseId}/legs/${legId}/points`, payload),
      ),
    onSuccess: invalidate,
  })
}

/** The same chain write with the leg as a mutation VARIABLE (S-10).
 *
 *  The leg page's one save writes the leg first and the chain second, and on
 *  `legs/new` the id exists only in the leg write's answer — so the id cannot be
 *  a hook argument, for `ApplyDirectionTemplateVars`' reason below: the
 *  mutation would close over the render's empty id. */
export function useReplaceLegPointsOf(caseId: string) {
  const invalidate = useInvalidateLeg(caseId)

  return useMutation<CaseLeg, Error, { legId: string; payload: LegPointsPayload }>({
    mutationFn: async ({ legId, payload }) =>
      toLegDetail(
        await api.put<LegDetailWire>(`/cases/${caseId}/legs/${legId}/points`, payload),
      ),
    onSuccess: invalidate,
  })
}

/** Which leg to overwrite, and which direction to copy onto it.
 *
 *  The LEG is a mutation variable rather than a hook argument, and that is not
 *  a style choice — it is the fix for a silent data loss. The case section
 *  creates a leg and applies a template to it in one gesture, so the id does
 *  not exist when the component renders: it arrives in the create's
 *  `onSuccess`. Holding it in state and firing in the same tick does NOT work,
 *  because `useMutation` hands the new options to its observer in a
 *  `useEffect` — the mutation that runs before React commits still closes over
 *  the previous render's id, which on a create is the empty string. The POST
 *  went to `/cases/{id}/legs//apply-template`, 404'd against a route that does
 *  not exist, and the reader — already navigated to the new leg — saw the
 *  chosen direction simply never land.
 *
 *  A variable cannot go stale that way: it is read at the call, not at the
 *  render. */
export interface ApplyDirectionTemplateVars {
  legId: string
  templateId: string
}

/** Copies a settings direction's points onto the leg (POST .../apply-template).
 *
 *  OVERWRITES the chain, which is why the page asks before it fires this: the
 *  points that go are the ones carrying the arrivals somebody recorded, and no
 *  undo exists for them. The warning is the control.
 *
 *  A COPY, not a link. Nothing on the leg keeps the template's id, so the
 *  direction can be edited or retired in Settings afterwards without moving a
 *  shipment that is already running — the rule `useDirectionTemplates` states
 *  from the other side. */
export function useApplyDirectionTemplate(caseId: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateLeg(caseId)

  return useMutation<CaseLeg, Error, ApplyDirectionTemplateVars>({
    mutationFn: async ({ legId, templateId }) =>
      toLegDetail(
        await api.post<LegDetailWire>(`/cases/${caseId}/legs/${legId}/apply-template`, {
          templateId,
        }),
      ),
    onSuccess: () => {
      invalidate()
      /* The answer is the whole leg, carrier included, and the counterparty
         statement and list count by a leg's carrier (S-22e) — so the finance
         keys they hang under refetch as well, as for every leg write. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
    },
  })
}
